import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Truck, ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const L = {
  direction: { move_in: 'Move-In', move_out: 'Move-Out' },
  occupancy: { empty: 'Empty', nearly_empty: 'Nearly empty', furnished: 'Furnished', not_sure: 'Not sure yet' },
  condition: { light: 'Light buildup', moderate: 'Moderate buildup', heavy: 'Heavy buildup', not_sure: 'Not sure' },
  detailing: { fridge_freezer: 'Refrigerator / freezer interiors', oven: 'Oven interior and racks', cabinets_drawers: 'Empty cabinet and drawer interiors' },
  flex: { yes: 'Yes', no: 'No', not_sure: 'Not sure' },
};
const fmtDeadline = (v) => (v ? new Date(v).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const Item = ({ label, children, wide, testId }) => (
  <div className={wide ? 'col-span-2' : ''}>
    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">{label}</p>
    <div className="text-sm text-zinc-900 whitespace-pre-wrap break-words" data-testid={testId}>{children || '—'}</div>
  </div>
);

const Photo = ({ quoteId, photo, headers }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    let objectUrl;
    axios.get(`${API_URL}/api/admin/quote-requests/${quoteId}/photos/${photo.id}`, { headers, responseType: 'blob' })
      .then((r) => { objectUrl = URL.createObjectURL(r.data); if (alive) setUrl(objectUrl); })
      .catch(() => alive && setFailed(true));
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [quoteId, photo.id, headers]);
  if (failed) return <div className="aspect-square rounded-lg bg-zinc-100 flex items-center justify-center text-[11px] text-zinc-500 p-2 text-center">Photo unavailable</div>;
  if (!url) return <div className="aspect-square rounded-lg bg-zinc-100 animate-pulse" />;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title={photo.filename} data-testid={`move-photo-${photo.id}`}>
      <img src={url} alt={photo.filename} className="aspect-square w-full object-cover rounded-lg border border-zinc-200" />
    </a>
  );
};

// Staff view of the Move-In / Move-Out answers saved with a quote request.
export const MoveDetailsSection = ({ quote, headers }) => {
  const m = quote.move_details;
  if (!m) return null;
  const size = [m.square_feet != null && `${m.square_feet} sq ft`, m.bedrooms != null && `${m.bedrooms} bedrooms`, m.bathrooms != null && `${m.bathrooms} bathrooms`].filter(Boolean).join(' · ');
  return (
    <div className="bg-sky-50/50 border border-sky-100 rounded-lg p-4 space-y-4" data-testid="move-details-block">
      <div className="flex items-center gap-2">
        <Truck className="w-4 h-4 text-sky-700" />
        <p className="text-sm font-semibold text-zinc-900">Move-In / Move-Out request · <span data-testid="move-detail-direction">{L.direction[m.direction]}</span></p>
        <span className="ml-auto text-[11px] font-semibold text-zinc-500">One-time</span>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Item label="Property address" wide testId="move-detail-address">{m.address}</Item>
        <Item label="Size">{size}</Item>
        <Item label="At time of cleaning" testId="move-detail-occupancy">{L.occupancy[m.occupancy]}</Item>
        <Item label="Current condition" testId="move-detail-condition">{L.condition[m.condition]}</Item>
        <Item label="Must be finished by" testId="move-detail-deadline">{fmtDeadline(m.completion_deadline)}</Item>
        <Item label="Preferred date flexible" testId="move-detail-flex">{L.flex[m.date_flexibility]}</Item>
        <Item label="Detailing requested · confirm in scope, not yet included" wide testId="move-detail-detailing">
          {(m.detailing_requested || []).map((d) => L.detailing[d]).join(', ')}
        </Item>
        <Item label="Spaces, belongings or special requests" wide testId="move-detail-scope-notes">{m.scope_notes}</Item>
        <Item label="Access, parking & other notes" wide testId="move-detail-access-notes">{m.access_notes}</Item>
      </div>
      <div>
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Property photos ({(m.photos || []).length})</p>
        {(m.photos || []).length === 0
          ? <p className="text-sm text-zinc-500">No photos added.</p>
          : <div className="grid grid-cols-4 gap-2" data-testid="move-detail-photos">{m.photos.map((p) => <Photo key={p.id} quoteId={quote.id} photo={p} headers={headers} />)}</div>}
      </div>
    </div>
  );
};
